

import React from "react";

const Button = ({
  variant = "primary",
  isLoading = false,
  onClick,
  type = "submit",
  children,
  size = "sm", 
  className = "",
  icon = null,
  loadingText = "Processing...",
  disabled = false,
  ...props
}) => {
  const buttonText = isLoading ? loadingText : children;
  
  const variantClasses = {
    primary: "bg-gradient-to-r from-violet-400 to-violet-700 hover:from-violet-700 hover:to-violet-400 text-white shadow-lg hover:shadow-xl",
    secondary: "bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-800 border-2 border-gray-300 hover:border-gray-400",
    danger: "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl",
    earning: "bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-yellow-900 shadow-lg hover:shadow-xl",
    info: "bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl",
    success: "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl",
    custom: "bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:shadow-md"
  };

  const sizeClasses = {
    xsm: "text-xs py-2 px-3 min-h-[32px]",
    sm: "text-sm py-3 px-6 min-h-[40px]",
    md: "text-base py-4 px-8 min-h-[48px]", 
    lg: "text-lg py-5 px-10 min-h-[56px]"
  };

  const baseClasses = `
    rounded-xl cursor-pointer font-semibold tracking-wide
    transition-all duration-300 ease-in-out
    transform hover:scale-[1.02] active:scale-[0.98]
    focus:outline-none focus:ring-4 focus:ring-opacity-50
    disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
    flex items-center justify-center gap-2
    relative overflow-hidden
  `;

  const focusClasses = {
    primary: "focus:ring-violet-500",
    secondary: "focus:ring-gray-400", 
    danger: "focus:ring-red-500",
    earning: "focus:ring-yellow-500",
    info: "focus:ring-cyan-500",
    success: "focus:ring-green-500",
    custom: "focus:ring-gray-400"
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${focusClasses[variant]}
        ${className}
      `}
      {...props}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
      )}

      {isLoading && (
        <div className="relative">
          <svg
            className="w-5 h-5 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}

      {/* Icon */}
      {icon && !isLoading && (
        <span className="relative flex items-center">
          {icon}
        </span>
      )}

      <span className="relative font-medium">
        {buttonText}
      </span>

      <div className="absolute inset-0 rounded-xl overflow-hidden">
        <div className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-300" />
      </div>
    </button>
  );
};

export default Button;